/**
 * SEO configuration for category pages (/cerco-lavoro/*)
 * Contains role, city, and occupation mappings with coordinates and meta content.
 */

// --- Role (domain_interest) mappings ---

export const ROLES: Record<string, { label: string; domainKey: string }> = {
  sala: { label: 'Sala', domainKey: 'sala' },
  cucina: { label: 'Cucina', domainKey: 'cucina' },
  bar: { label: 'Bar', domainKey: 'bar' },
  hotel: { label: 'Hotel', domainKey: 'reception' },
};

export const ALL_ROLE_SLUGS = Object.keys(ROLES);

// --- City mappings with coordinates ---

export interface CityConfig {
  label: string;
  lat: number;
  lon: number;
  radius: number;
  zoom: number;
}

export const CITIES: Record<string, CityConfig> = {
  milano: { label: 'Milano', lat: 45.458655731657956, lon: 9.180887287380187, radius: 40000, zoom: 10 },
  roma: { label: 'Roma', lat: 41.902782, lon: 12.496366, radius: 40000, zoom: 10 },
  torino: { label: 'Torino', lat: 45.070312, lon: 7.686856, radius: 40000, zoom: 10 },
  bologna: { label: 'Bologna', lat: 44.494887, lon: 11.342616, radius: 40000, zoom: 10 },
  genova: { label: 'Genova', lat: 44.40565, lon: 8.946256, radius: 40000, zoom: 10 },
  firenze: { label: 'Firenze', lat: 43.769562, lon: 11.255814, radius: 40000, zoom: 10 },
  bergamo: { label: 'Bergamo', lat: 45.698265, lon: 9.67727, radius: 40000, zoom: 10 },
  brescia: { label: 'Brescia', lat: 45.541553, lon: 10.211802, radius: 40000, zoom: 10 },
  napoli: { label: 'Napoli', lat: 40.851798, lon: 14.26812, radius: 40000, zoom: 10 },
  venezia: { label: 'Venezia', lat: 45.440847, lon: 12.315515, radius: 40000, zoom: 10 },
  verona: { label: 'Verona', lat: 45.438384, lon: 10.991622, radius: 40000, zoom: 10 },
  como: { label: 'Como', lat: 45.808042, lon: 9.085176, radius: 40000, zoom: 10 },
  catania: { label: 'Catania', lat: 37.507879, lon: 15.083029, radius: 40000, zoom: 10 },
  cagliari: { label: 'Cagliari', lat: 39.223841, lon: 9.121661, radius: 40000, zoom: 10 },
};

export const ALL_CITY_SLUGS = Object.keys(CITIES);

// --- Special location slugs ---

export const SPECIAL_LOCATIONS: Record<string, string> = {
  estero: 'Estero',
  stagionale: 'per lavori stagionali',
};

// --- Occupation macro-page mappings ---

export interface OccupationPageConfig {
  label: string;
  occupationIds: string[];
  metaLabel: string;
  domainKeys: string[]; // domain_interest keys these occupations belong to
}

export const OCCUPATION_PAGES: Record<string, OccupationPageConfig> = {
  cameriere: {
    label: 'Cameriere',
    occupationIds: ['50', '51', '49'], // Cameriere, Addetto sala, Runner
    metaLabel: 'Cameriere',
    domainKeys: ['sala'],
  },
  cuoco: {
    label: 'Cuoco',
    occupationIds: ['7', '4', '8', '5'], // Cuoco capo partita, Aiuto cuoco, Cuoco unico, Addetto cucina
    metaLabel: 'Cuoco',
    domainKeys: ['cucina'],
  },
  pizzaiolo: {
    label: 'Pizzaiolo',
    occupationIds: ['15', '13', '17'], // Pizzaiolo, Aiuto pizzaiolo, Capo pizzaiolo
    metaLabel: 'Pizzaiolo',
    domainKeys: ['cucina'],
  },
  barista: {
    label: 'Barista',
    occupationIds: ['35', '38', '25', '36'], // Banconista bar, Bartender, Addetto banco, Addetto caffetteria
    metaLabel: 'Barista',
    domainKeys: ['bar'],
  },
  chef: {
    label: 'Chef',
    occupationIds: ['6', '53', '10', '11'], // Sous chef, Chef de rang, Head chef, Chef executive
    metaLabel: 'Chef',
    domainKeys: ['cucina', 'sala'], // Sous chef/Head chef = cucina, Chef de rang = sala
  },
};

export const ALL_OCCUPATION_SLUGS = Object.keys(OCCUPATION_PAGES);

// Cities with enough volume for occupation+city combos
export const OCCUPATION_CITY_COMBOS = ['milano', 'roma', 'torino'];

// --- Accommodation benefit keys ---

export const ACCOMMODATION_BENEFIT_KEYS = [
  'seasonal_accommodation',
  'seasonal_accommodation_shared',
  'seasonal_accommodation_single',
];

// --- SEO content generation ---

const ROLE_SEO_TITLES: Record<string, string> = {
  sala: 'Lavoro Sala',
  cucina: 'Lavoro Cucina',
  bar: 'Lavoro Bar',
  hotel: 'Lavoro Hotel',
};

const ROLE_SEO_DESCRIPTIONS: Record<string, string> = {
  sala: 'Su Restworld trovi solo offerte di lavoro sala{city} verificate, orari rispettati e stipendi giusti. Scegli il locale adatto a te e inizia subito.',
  cucina:
    'Su Restworld trovi solo offerte di lavoro cucina{city} verificate, orari rispettati e stipendi giusti. Scegli il locale adatto a te e inizia subito.',
  bar: 'Su Restworld trovi solo offerte di lavoro bar{city} verificate, orari rispettati e stipendi giusti. Scegli il locale adatto a te e inizia subito.',
  hotel:
    'Su Restworld trovi solo offerte di lavoro hotel{city} verificate, orari rispettati e stipendi giusti. Scegli il locale adatto a te e inizia subito.',
};

export interface CategorySeoMeta {
  metaTitle: string;
  metaDescription: string;
  /**
   * Locale-free, because the page hands it to the layout and to the breadcrumb,
   * and both add the prefix of the locale being served. An absolute Italian URL
   * used to live here too, until the breadcrumb stopped naming the Italian
   * version on `/en/`.
   */
  canonicalPath: string;
  keywords: string;
}

export function buildCategorySeoMeta(params: {
  role?: string;
  location?: string;
  occupationSlug?: string;
  isAbroad?: boolean;
  isSeasonal?: boolean;
  isAccommodation?: boolean;
  count?: number;
}): CategorySeoMeta {
  const { role, location, occupationSlug, isAbroad, isSeasonal, isAccommodation, count } = params;

  const cityLabel = location ? CITIES[location]?.label || SPECIAL_LOCATIONS[location] || location : '';

  const countSuffix = count !== undefined && count > 0 ? `: ${count} offerte` : '';

  let metaTitle = '';
  let metaDescription = '';
  let canonicalPath = '/cerco-lavoro';

  if (occupationSlug) {
    const occ = OCCUPATION_PAGES[occupationSlug];
    const occLabel = occ?.metaLabel || occupationSlug;
    if (cityLabel && location) {
      metaTitle = `Lavoro ${occLabel} a ${cityLabel}${countSuffix}`;
      metaDescription = `Cerchi lavoro come ${occLabel.toLowerCase()} a ${cityLabel}? Su Restworld trovi offerte verificate con orari rispettati e stipendi giusti.`;
      canonicalPath += `/${occupationSlug}/${location}`;
    } else {
      metaTitle = `Lavoro ${occLabel}${countSuffix}`;
      metaDescription = `Cerchi lavoro come ${occLabel.toLowerCase()}? Su Restworld trovi offerte verificate con orari rispettati e stipendi giusti.`;
      canonicalPath += `/${occupationSlug}`;
    }
  } else if (isAccommodation) {
    metaTitle = `Lavoro stagionale con alloggio${countSuffix}`;
    metaDescription =
      'Cerchi lavoro stagionale con alloggio incluso? Su Restworld trovi offerte verificate nella ristorazione con vitto e alloggio.';
    // The published page is `stagionale-con-alloggio`, with a hyphen, and it
    // lives in the marketing app. `/cerco-lavoro/stagionale/con-alloggio`
    // answers **404** on the canonical host: the negative lookahead shared by
    // the redirect and the rewrite in website `vercel.json` allows anything
    // under `stagionale/`, so this path is neither redirected to the hub nor
    // proxied to worker-app, and `SEO_PAGES` has no slug for it. Measured on
    // 06/08/2026: `/cerco-lavoro/stagionale/con-alloggio` -> 404,
    // `/cerco-lavoro/stagionale-con-alloggio` -> 200.
    canonicalPath += '/stagionale-con-alloggio';
  } else if (role && isSeasonal) {
    metaTitle = `${ROLE_SEO_TITLES[role] || `Lavoro ${role}`} stagionale${countSuffix}`;
    metaDescription = (
      ROLE_SEO_DESCRIPTIONS[role] ||
      'Su Restworld trovi solo offerte di lavoro{city} verificate, orari rispettati e stipendi giusti.'
    ).replace('{city}', ' stagionale');
    canonicalPath += `/${role}/stagionale`;
  } else if (role && isAbroad) {
    metaTitle = `${ROLE_SEO_TITLES[role] || `Lavoro ${role}`} all'estero${countSuffix}`;
    metaDescription = (
      ROLE_SEO_DESCRIPTIONS[role] ||
      'Su Restworld trovi solo offerte di lavoro{city} verificate, orari rispettati e stipendi giusti.'
    ).replace('{city}', " all'estero");
    canonicalPath += `/${role}/estero`;
  } else if (role && cityLabel) {
    metaTitle = `${ROLE_SEO_TITLES[role] || `Lavoro ${role}`} a ${cityLabel}${countSuffix}`;
    metaDescription = (
      ROLE_SEO_DESCRIPTIONS[role] ||
      'Su Restworld trovi solo offerte di lavoro{city} verificate, orari rispettati e stipendi giusti.'
    ).replace('{city}', ` a ${cityLabel}`);
    canonicalPath += `/${role}/${location}`;
  } else if (role) {
    metaTitle = `${ROLE_SEO_TITLES[role] || `Lavoro ${role}`}${countSuffix}`;
    metaDescription = (
      ROLE_SEO_DESCRIPTIONS[role] ||
      'Su Restworld trovi solo offerte di lavoro verificate, orari rispettati e stipendi giusti.'
    ).replace('{city}', '');
    canonicalPath += `/${role}`;
  } else if (isSeasonal) {
    metaTitle = `Lavoro stagionale nella ristorazione${countSuffix}`;
    metaDescription =
      'Cerchi un lavoro stagionale nella ristorazione? Su Restworld trovi offerte verificate per la stagione estiva e invernale, con condizioni chiare.';
    canonicalPath += '/stagionale';
  } else if (isAbroad) {
    metaTitle = `Lavoro nella ristorazione all'estero${countSuffix}`;
    metaDescription =
      "Vuoi lavorare nella ristorazione all'estero? Su Restworld trovi offerte verificate in Europa e nel mondo.";
    canonicalPath += '/estero';
  } else if (cityLabel && location) {
    metaTitle = `Lavoro nella ristorazione a ${cityLabel}${countSuffix}`;
    metaDescription = `Su Restworld trovi solo offerte di lavoro a ${cityLabel} verificate, orari rispettati e stipendi giusti. Scegli il locale adatto a te e inizia subito.`;
    canonicalPath += `/${location}`;
  } else {
    metaTitle = 'Trova il tuo prossimo lavoro nella ristorazione';
    metaDescription =
      'Su Restworld trovi solo offerte verificate, orari rispettati e stipendi giusti. Scegli il locale adatto a te e inizia subito.';
  }

  const keywords = [
    'lavoro ristorazione',
    'offerte lavoro',
    role || '',
    occupationSlug ? OCCUPATION_PAGES[occupationSlug]?.metaLabel : '',
    cityLabel,
    isAbroad ? 'estero' : '',
    isSeasonal ? 'stagionale' : '',
    isAccommodation ? 'alloggio' : '',
  ]
    .filter(Boolean)
    .join(', ');

  return { metaTitle, metaDescription, canonicalPath, keywords };
}

// --- SEO intro texts per category ---

export const CATEGORY_INTRO_TEXTS: Record<string, string> = {
  // Roles
  sala: "Scopri le offerte di lavoro in sala disponibili su Restworld. Dalle posizioni di cameriere fino a maître e responsabile di sala, trova l'opportunità giusta per la tua carriera nella ristorazione.",
  cucina:
    'Esplora le offerte di lavoro in cucina su Restworld. Che tu sia un aiuto cuoco alle prime armi o uno chef esperto, trovi posizioni verificate con condizioni chiare e trasparenti.',
  bar: 'Trova il tuo prossimo lavoro al bar o bancone su Restworld. Offerte per baristi, banconisti e bartender con orari e stipendi verificati.',
  hotel:
    "Cerca lavoro nel settore alberghiero su Restworld. Posizioni per receptionist, camerieri ai piani e altre figure dell'ospitalità con condizioni trasparenti.",
  // Special
  stagionale:
    'Cerchi un lavoro stagionale nella ristorazione? Su Restworld trovi offerte verificate per la stagione estiva e invernale, con condizioni chiare fin da subito.',
  'stagionale/con-alloggio':
    'Lavoro stagionale con alloggio incluso: trova offerte verificate su Restworld. Vitto e alloggio garantiti per lavorare in tutta tranquillità durante la stagione.',
  estero:
    "Vuoi lavorare nella ristorazione all'estero? Su Restworld trovi offerte verificate in Europa e nel mondo, con tutti i dettagli su contratto e condizioni.",
  // Role + special location combos
  'sala/stagionale':
    'Cerchi lavoro stagionale in sala? Offerte verificate per camerieri, chef de rang e addetti sala per la stagione estiva e invernale.',
  'cucina/stagionale':
    'Lavoro stagionale in cucina: trova offerte verificate per cuochi, aiuto cuochi e chef per la stagione su Restworld.',
  'bar/stagionale':
    'Offerte di lavoro stagionale al bar: posizioni per baristi e banconisti con condizioni chiare per la stagione.',
  'hotel/stagionale':
    "Lavoro stagionale in hotel: trova offerte per receptionist e figure dell'ospitalità per la stagione estiva e invernale.",
  'sala/estero':
    "Cerchi lavoro in sala all'estero? Su Restworld trovi offerte verificate per camerieri e figure di sala in Europa e nel mondo.",
  'cucina/estero':
    "Lavoro in cucina all'estero: offerte verificate per cuochi e chef in ristoranti europei e internazionali.",
  'bar/estero': "Offerte di lavoro al bar all'estero: posizioni per baristi e bartender con condizioni trasparenti.",
  'hotel/estero':
    "Lavoro in hotel all'estero: trova posizioni per receptionist e figure alberghiere in Europa e nel mondo.",
  // Occupations
  cameriere:
    'Cerchi lavoro come cameriere? Su Restworld trovi offerte verificate per camerieri, addetti sala e runner con orari rispettati e stipendi trasparenti.',
  cuoco:
    "Sei un cuoco in cerca di lavoro? Esplora le offerte su Restworld: dal cuoco capo partita all'aiuto cuoco, posizioni verificate con condizioni chiare.",
  pizzaiolo:
    'Cerchi lavoro come pizzaiolo? Su Restworld trovi offerte verificate per pizzaioli e aiuto pizzaioli con condizioni trasparenti.',
  barista:
    'Lavoro come barista: scopri le offerte su Restworld. Posizioni per banconisti, bartender e addetti caffetteria con orari e stipendi verificati.',
  chef: 'Sei uno chef in cerca di nuove opportunità? Su Restworld trovi posizioni per sous chef, chef de rang e head chef con condizioni verificate.',
};
